/**
 * Telegram Mini App SDK Integration using @tma.js/sdk-react
 * @see https://docs.telegram-mini-apps.com/packages/tma-js-sdk-react
 */

import {
  backButton,
  closingBehavior,
  cloudStorage,
  hapticFeedback,
  init,
  mainButton,
  miniApp,
  popup,
  qrScanner,
  retrieveLaunchParams,
  retrieveRawInitData,
  secondaryButton,
  settingsButton,
  swipeBehavior,
  themeParams,
  type User,
  viewport,
} from '@tma.js/sdk-react';

// Initialize the SDK once
let initialized = false;

export function initTelegramSDK() {
  if (initialized) return;
  initialized = true;

  try {
    init();
    // Mount core components
    miniApp.mount();
    viewport.mount();
    themeParams.mount();
  } catch {
    // Telegram SDK initialization failed silently
  }
}

// Export SDK components for direct access
export {
  backButton,
  closingBehavior,
  cloudStorage,
  hapticFeedback,
  mainButton,
  miniApp,
  popup,
  qrScanner,
  secondaryButton,
  settingsButton,
  swipeBehavior,
  themeParams,
  viewport,
};

// Helper functions
export function getRawInitData(): string | undefined {
  return retrieveRawInitData();
}

export function getLaunchParams() {
  try {
    return retrieveLaunchParams();
  } catch {
    return null;
  }
}

export function getInitData() {
  try {
    const launchParams = retrieveLaunchParams();
    return launchParams.tgWebAppData || null;
  } catch {
    return null;
  }
}

export function getUser(): User | null {
  return getInitData()?.user || null;
}

export function isTelegramEnvironment(): boolean {
  try {
    retrieveLaunchParams();
    return true;
  } catch {
    return false;
  }
}

// Theme helpers
export function getThemeColors() {
  return {
    backgroundColor: themeParams.bgColor?.() || '#1a1a1a',
    textColor: themeParams.textColor?.() || '#ffffff',
    hintColor: themeParams.hintColor?.() || '#7d7d7d',
    linkColor: themeParams.linkColor?.() || '#62c9ff',
    buttonColor: themeParams.buttonColor?.() || '#5288c1',
    buttonTextColor: themeParams.buttonTextColor?.() || '#ffffff',
    secondaryBackgroundColor: themeParams.secondaryBgColor?.() || '#2b2b2b',
  };
}

// Color scheme detection
export function getColorScheme(): 'light' | 'dark' {
  const bgColor = themeParams.bgColor?.() || '#1a1a1a';
  // Simple dark detection based on background color
  return bgColor.toLowerCase().includes('#1') ||
    bgColor.toLowerCase().includes('#2')
    ? 'dark'
    : 'light';
}

// Ready signal - call when app is ready to display
export function signalReady() {
  miniApp.ready();
}

// Expand to full height
export function expandApp() {
  viewport.expand();
}

// Close the app
export function closeApp(returnBack = false) {
  miniApp.close(returnBack);
}
